/* eslint-disable i18next/no-literal-string -- later */
import { useRequestPayoutsMutation } from '@/affiliate-dashboard/graphql';
import { Button } from '@/components/base/button';
import { PageHeading } from '@/components/base/page-heading';
import Section from '@/components/base/section';
import { faker } from '@faker-js/faker';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { enqueueSnackbar } from 'notistack';
import { type FormEvent } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

const userSchema = z.object({
  date: z.string().min(1, { message: 'date is required' }),
});

type User = z.infer<typeof userSchema>;

export function RequestPayout(): JSX.Element {
  const [requestPayouts, loading] = useRequestPayoutsMutation();

  const methods = useForm<User>({
    resolver: zodResolver(userSchema),
    mode: 'onChange',
    defaultValues: {
      date: format(faker.date.recent(), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
    },
  });
  const { handleSubmit, control } = methods;
  async function onSubmit(values: User): Promise<void> {
    try {
      await requestPayouts({
        variables: {
          to: values.date,
        },
      });
      enqueueSnackbar({
        message: `Payouts requested`,
        variant: 'success',
      });
    } catch (e) {
      if (e instanceof Error) {
        enqueueSnackbar({
          message: e.message,
          variant: 'error',
        });
      }
    }
  }

  function handleClickSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    return void (async () => {
      handleSubmit(onSubmit)();
    })();
  }

  return (
    <Section variant="secondary">
      <div className="flex w-full flex-col">
        <PageHeading>Request Payouts</PageHeading>
        <FormProvider {...methods}>
          <form onSubmit={handleClickSubmit} data-testid="register-form">
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-5">
                <div className="flex flex-col">
                  <label htmlFor="date" className="mb-2 text-sm font-bold">
                    Date
                  </label>
                  <Controller
                    name="date"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        selected={field.value ? new Date(field.value) : null}
                        onChange={(date: Date | null) => {
                          field.onChange(date ? date.toISOString() : '');
                        }}
                        value={
                          field.value
                            ? format(new Date(field.value), 'dd.MM.yyyy')
                            : ''
                        }
                        dateFormat="dd.MM.yyyy"
                        className="w-fit rounded border p-2 text-black"
                        placeholderText="Select a date"
                      />
                    )}
                  />
                </div>
              </div>
              <Button
                as="button"
                className="text-base font-bold"
                loading={loading.loading}
                type="submit"
                data-testid="registration-submit-button"
              >
                Request payout
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </Section>
  );
}
